import { Link } from 'react-router-dom'
import { PageShell } from '@/components/ui/site-chrome'
import type { LegalPage } from '@/data/content'

/* ================================================================== */
/*  Legal: a single template for the policy pages (privacy, terms,     */
/*  instructor). Content lives in data/content.ts, sourced from the     */
/*  client's existing policies. Quiet, readable, on the Night Glass     */
/*  theme with the shared nav + footer.                                 */
/* ================================================================== */

function Block({ block }: { block: LegalPage['sections'][number]['blocks'][number] }) {
  if (block.kind === 'p') {
    return <p className="mt-4 text-[15px] xl:text-[16px] leading-relaxed text-lc-mute">{block.text}</p>
  }
  if (block.kind === 'list') {
    return (
      <ul className="mt-4 space-y-2.5">
        {block.items.map((it) => (
          <li key={it} className="flex gap-3 text-[15px] xl:text-[16px] leading-relaxed text-lc-mute">
            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-lime" aria-hidden="true" />
            {it}
          </li>
        ))}
      </ul>
    )
  }
  return (
    <div className="mt-5 border-l-2 border-lime/40 pl-4">
      <p className="label !text-[11px] text-lc-accent">{block.label}</p>
      <p className="mt-1.5 text-[15px] xl:text-[16px] leading-relaxed text-lc-mute">{block.text}</p>
    </div>
  )
}

export default function Legal({ page }: { page: LegalPage }) {
  return (
    <PageShell>
      {/* hero */}
      <section className="px-5 pb-12 pt-36 md:pt-44">
        <div className="container-lc max-w-3xl">
          <p className="label text-lc-mute">{page.kicker}</p>
          <h1 className="mt-4 font-display text-5xl">{page.title}</h1>
          <p className="mt-4 label !text-[11px] text-lc-accent">{page.updated}</p>
          <p className="mt-6 text-lg leading-relaxed text-lc-mute">{page.intro}</p>
        </div>
      </section>

      {/* sections */}
      <section className="px-5 pb-24">
        <div className="container-lc max-w-3xl">
          <div className="lc-glass rounded-card p-5 xl:p-7 md:p-10">
            {page.sections.map((s, i) => (
              <div key={s.n} className={i > 0 ? 'mt-10 border-t border-white/10 pt-10' : ''}>
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-2xl text-lc-accent">{s.n}</span>
                  <h2 className="font-display text-2xl">{s.heading}</h2>
                </div>
                {s.blocks.map((b, j) => (
                  <Block key={j} block={b} />
                ))}
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <p className="text-[15px] text-lc-mute">Questions about this policy?</p>
            <Link
              to="/contact"
              className="rounded-pill bg-lime px-6 py-3 text-[15px] font-bold text-night transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100"
            >
              Contact support
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  )
}
